<template>
  <vue-tel-input
    :model-value="modelValue"
    @update:model-value="onInput"
    @blur="onBlur"
    :class="classes"
    :valid-characters-only="true"
    :preferredCountries="['DK','ES','FR','GB','NO','SE']"
    :dropdown-options="{ showFlags: true }"
    :input-options="{ maxlength: 15, showDialCode: true }"
  />
</template>

<script lang="ts" setup>
import { VueTelInput } from 'vue-tel-input'
import 'vue-tel-input/vue-tel-input.css';
import { parsePhoneNumberFromString } from 'libphonenumber-js'

type Props = {
  modelValue?: string
  placeholder?: string
  type?: string
  disabled?: boolean
  readOnly?: boolean
  multiline?: boolean
  rows?: number
  fullWidth?: boolean
  autoFocus?: boolean
  error?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  placeholder: undefined,
  type: 'text',
  rows: undefined
})

const emit = defineEmits<{
  (event: 'update:modelValue', value: string): void
  (event: 'blur'): void
}>()

const classes = computed(() => ({
  'app-text-field': true,
  'app-text-field--error': props.error,
  'app-text-field--full-width': props.fullWidth
}))


function validate(v:string) {
  const p = parsePhoneNumberFromString(v||'')
  emit('valid', p?.isValid() ?? false)
}

function onInput(v:string) {
  const phoneClean = v.replace(/\s+/g, '')
  emit('update:modelValue', phoneClean)
  validate(phoneClean)
}

function onBlur() {
  validate(props.modelValue || '')
}

const input = ref<HTMLInputElement | HTMLTextAreaElement | null>(null)

onMounted(() => {
  nextTick(() => {
    if (props.autoFocus) input.value?.focus()
  })
})
</script>

<style lang="scss">
@import '@boats/ui/styles/config';

.vti__dropdown.open {
  background-color: inherit;
}

.vti__dropdown:hover {
  background-color: inherit;
}

.app-text-field {
  display: inline-block;
  padding: 0.8125rem 0.75rem;
  font-family: $font-barlow;
  font-size: 0.875rem;
  line-height: 1.42857143;
  color: $black;
  background-color: $gray-100;
  border: 1px solid $gray-200;
  border-radius: 0.125rem;
  @include transition('color, background-color, border-color');

  input {
    background-color: $gray-100;
  }

  &::placeholder {
    color: $gray-400;
  }

  &:hover {
    border-color: $gray-300;
  }

  &.app-text-field--error {
    background-color: $white;
    border-color: $signal-error-red;

    input {
      background-color: inherit;
    }
  }

  @include media-breakpoint-up(md) {
    padding: 1rem;
    font-size: 1rem;
    line-height: 1.25;
  }
}

.app-text-field--full-width {
  display: flex;
  width: 100%;
}
</style>
